'use client';

import { useContext, useEffect, useState } from 'react';

import {
    Align,
    AutoComplete,
    Button,
    ButtonVariant,
    Input,
    InputChangeEventWeb,
    InputStatus,
    ItemSelectedEvent,
    Justify,
    Spacer,
    SpacerSize,
    StatusState,
    Text,
    Title,
    TitleLevels,
    ToasterContext,
    TypographyBold,
    View,
} from '@bytel/trilogy-react';

import { googleMapsAutocompleteService } from '@services/google-maps-autocomplete-service';
import { logger } from '@services/loggers';

import { CustomerAddress, GMAddressField, GoogleMapsAutocompleteItem } from '@app-types/delivery';
import { TagEventType } from '@app-types/tracking';

import { Loader } from '@components/loader';

import useTracking from '@hooks/use-tracking';

import { GM_ADDRESS_FIELD } from '@constants/address-form';
import { tagClickAddressNotFound, tagGeolocalisation } from '@constants/tags';

import { GCPForm } from '@tunnel/components/common';
import { useDebouncedCallback } from 'use-debounce';

type AddressFormProps = {
    address: CustomerAddress | null;
    setAddress: (address: CustomerAddress | null) => void;
    label: string;
    isFormTouched: boolean;
    setIsFormTouched: (isUpdatingAddressDetails: boolean) => void;
    displayGeolocation: boolean;
    displayGCPFormWidget: boolean;
    displayGCPForm?: boolean;
    setDisplayGCPForm?: (displayGCPForm: boolean) => void;
    isDeliveryLoading?: boolean;
};

export function AddressForm({
    address,
    setAddress,
    label,
    isFormTouched,
    setIsFormTouched,
    displayGeolocation,
    displayGCPFormWidget,
    displayGCPForm,
    setDisplayGCPForm,
    isDeliveryLoading,
}: Readonly<AddressFormProps>) {
    const { show } = useContext(ToasterContext);
    const { sendTag } = useTracking();
    const [addressField, setAddressField] = useState({ ...GM_ADDRESS_FIELD });
    const [pendingAddress, setPendingAddress] = useState<CustomerAddress | null>(null);
    const [complementaryAddress, setComplementaryAddress] = useState('');
    const [isGeolocationLoading, setIsGeolocationLoading] = useState(false);

    const debouncedGetPredictions = useDebouncedCallback((value) => getMapsAutocompletePredictions(value), 250);

    useEffect(() => {
        if (address) {
            const clientAddressField: GMAddressField = {
                ...GM_ADDRESS_FIELD,
                value: `${address.streetNumber} ${address.streetName} ${address.postalCode}, ${address.city}`.trim(),
                status: 'success',
                isSelectedFromAutocomplete: true,
                errorMessage: '',
            };
            setAddressField(clientAddressField);
            setPendingAddress(address);
        }
    }, [address]);

    const onFormSuccessStatus = (value: string) => {
        const field: GMAddressField = {
            ...addressField,
            value,
            status: 'success',
            isSelectedFromAutocomplete: true,
            errorMessage: '',
        };
        if (!value) field.data = [];

        setAddressField(field);
    };

    const onAddressInputChange = (value: string) => {
        const field: GMAddressField = {
            ...addressField,
            value,
            status: 'default',
            isSelectedFromAutocomplete: false,
            errorMessage: '',
        };
        setAddressField(field);

        setPendingAddress(null);
        setComplementaryAddress('');
        !isFormTouched && setIsFormTouched(true);
        address && setAddress(null);
        displayGCPForm && setDisplayGCPForm?.(false);
    };

    const onGoogleMapsAutocompleteError = (error: unknown) => {
        show({
            title: 'Une erreur est survenue lors de la recherche de votre adresse',
            status: StatusState.WARNING,
        });
        setDisplayGCPForm?.(true);

        logger.error('onGoogleMapsAutocomplete', error);
    };

    const getMapsAutocompletePredictions = async (query: string) => {
        try {
            const autocompleteData = await googleMapsAutocompleteService.getAddresses(query);

            setAddressField((prev) => ({ ...prev, data: autocompleteData }));
        } catch (error) {
            onGoogleMapsAutocompleteError(error);
        }
    };

    const handleChange = (e: InputChangeEventWeb) => {
        onAddressInputChange(e.inputValue);

        debouncedGetPredictions(e.inputValue);
    };

    const handleItemSelected = async (e: ItemSelectedEvent<GoogleMapsAutocompleteItem | null>) => {
        if (!e.value) return;
        onFormSuccessStatus(e.value?.label ?? '');

        try {
            const { address } = await googleMapsAutocompleteService.getAddressDetails({
                placeId: e.value.data.place_id,
            });

            setPendingAddress(address);
        } catch (error) {
            onGoogleMapsAutocompleteError(error);
        }
    };

    const handleGeolocation = () => {
        sendTag(tagGeolocalisation, TagEventType.CLICK);
        onAddressInputChange('');
        setIsGeolocationLoading(true);

        const options = {
            enableHighAccuracy: true,
            timeout: 6000,
            maximumAge: 10000,
        };

        const success = async (position: GeolocationPosition) => {
            const { latitude, longitude } = position.coords;

            try {
                const { formattedAddress, address } = await googleMapsAutocompleteService.getAddressDetails({
                    location: { lat: latitude, lng: longitude },
                });

                onFormSuccessStatus(formattedAddress);

                setPendingAddress(address);
            } catch (error) {
                onGoogleMapsAutocompleteError(error);
            } finally {
                setIsGeolocationLoading(false);
            }
        };

        const error = (error: GeolocationPositionError) => {
            if (error.message === 'User denied Geolocation') {
                show({
                    title: 'Veuillez autoriser la géolocalisation',
                    status: StatusState.WARNING,
                });
            } else {
                onGoogleMapsAutocompleteError(error);
            }

            setIsGeolocationLoading(false);
        };

        navigator.geolocation.getCurrentPosition(success, error, options);
    };

    const handleComplementaryAddressInputChange = ({ inputValue }: InputChangeEventWeb) => {
        setComplementaryAddress(inputValue);
        setPendingAddress((prev) => (prev ? { ...prev, complementaryAddress: inputValue } : null));
    };

    const handleValidateAddress = () => {
        if (pendingAddress) {
            setAddress(pendingAddress);
        }
    };

    return (
        <div>
            {isDeliveryLoading ? (
                <View flexable justify={Justify.CENTER} className='is-fullheight' align={Align.CENTER}>
                    <Loader />
                </View>
            ) : (
                <>
                    <Title level={TitleLevels.FIVE}>{label}</Title>
                    <AutoComplete
                        value={addressField.value}
                        data={addressField.data}
                        onChange={handleChange}
                        onItemSelected={handleItemSelected}
                        displayMenu={false}
                        placeholder={label}
                        autoCompleteType='street-address'
                        status={addressField.status}
                        help={addressField.errorMessage ?? undefined}
                        matching={(data) => data}
                        loading={isGeolocationLoading}
                    />

                    {displayGeolocation && (
                        <Text>
                            <button className='button-clear link' onClick={handleGeolocation}>
                                Localisez-moi
                            </button>
                        </Text>
                    )}
                    <Spacer size={SpacerSize.FOUR} />

                    <Input
                        onChange={handleComplementaryAddressInputChange}
                        placeholder="Complément d'adresse"
                        type='text'
                        status={complementaryAddress ? InputStatus.SUCCESS : InputStatus.DEFAULT}
                        disabled={!pendingAddress}
                    />
                    <Spacer size={SpacerSize.FOUR} />
                    <Button
                        data-testid='confirm_address'
                        variant={ButtonVariant.PRIMARY}
                        onClick={handleValidateAddress}
                        disabled={!pendingAddress}
                    >
                        Valider mon adresse
                    </Button>
                </>
            )}

            {displayGCPFormWidget && (
                <>
                    {!isDeliveryLoading && (
                        <>
                            <Text typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>Vous ne trouvez pas votre adresse ?</Text>
                            <Text>
                                <button
                                    className='button-clear link'
                                    onClick={() => {
                                        setDisplayGCPForm?.(!displayGCPForm);
                                        if (setDisplayGCPForm && !displayGCPForm) {
                                            sendTag(tagClickAddressNotFound, TagEventType.CLICK);
                                        }

                                        setPendingAddress(null);
                                        setAddress(null);
                                        setAddressField({ ...GM_ADDRESS_FIELD });
                                    }}
                                >
                                    Regardons ensemble
                                </button>
                            </Text>
                        </>
                    )}
                    {displayGCPForm && (
                        <View style={{ display: !isDeliveryLoading ? 'block' : 'none' }}>
                            <GCPForm setAddress={setAddress} />
                        </View>
                    )}
                </>
            )}
        </div>
    );
}
